<script setup>
import {defineProps, ref, watchEffect} from "vue";
import {convertTimestampToData} from "@/tools/date-time-tools";
import ArrowBottomIcon from "@/components/Icons/ArrowBottomIcon.vue";
import ArrowTopIcon from "@/components/Icons/ArrowTopIcon.vue";
import GoodIcon from "@/components/Icons/GoodIcon.vue";
import SadIcon from "@/components/Icons/SadIcon.vue";
import NeutralIcon from "@/components/Icons/NeutralIcon.vue";
import {t} from "@/controllerLK/GlobusLocalization";
import {useNotificationsStore} from "@/store/notifications";
import LocationIcon from "@/components/Icons/LocationIcon.vue";
import IconTranslate from "@/components/Icons/IconTranslate.vue";
import IconTranslateFixed from "@/components/Icons/IconTranslateFixed.vue";
import LocalLoader from "@/components/UI/LocalLoader.vue";
import { useNewsFeedStore } from "@/store/NewsFeed/newsFeed";
import {importanceList} from "@/const/importance.list";

const props = defineProps({
  item: {
    type: Object,
    required: true
  }
});

const isCardOpened = ref(false);
const store = useNotificationsStore();
const newsFeedStore = useNewsFeedStore();

const isActiveTranslateNewsText = ref(false);
const isActiveNewsTextTranslated = ref(false);
const translatedNewsTitle = ref("");
const translatedNewsText = ref("");

watchEffect(() => {
  if (props.item) {
    translatedNewsTitle.value = props.item.title;
    translatedNewsText.value = props.item.text;
  }
});

const translateNewsText = async () => {
  isActiveTranslateNewsText.value = true;
  if (isActiveNewsTextTranslated.value) {
    translatedNewsTitle.value = props.item.title;
    translatedNewsText.value = props.item.text;
    isActiveNewsTextTranslated.value = false;
  } else {
    const translated = await newsFeedStore.translateNewsText(
        translatedNewsTitle.value,
        translatedNewsText.value,
        props.item.id
    );

    translatedNewsTitle.value = translated.title;
    translatedNewsText.value = translated.raw_text;
    isActiveNewsTextTranslated.value = true;
  }
  isActiveTranslateNewsText.value = false;
};

const saveIdToClipboard = async (id) => {
  try {
    await navigator.clipboard.writeText("id: " + id);
    store.createNotify(t("map_page.toast.clipboard_success"), "success");
  } catch (e) {
    console.log(e);
    store.createNotify(t("map_page.toast.clipboard_error"), "success");
  }
};

</script>

<template>
  <div class="news-card">
    <div class="news-card-header">
      <div class="news-card-header__title">
        {{ translatedNewsTitle }}
      </div>
      <div
        class="news-card-header__button"
        :class="{'news-card-header__button--active': isActiveNewsTextTranslated}"
        @click="translateNewsText"
      >
        <LocalLoader
          v-if="isActiveTranslateNewsText"
          style="width: 18px; height: 18px"
        />
        <template v-else>
          <IconTranslateFixed v-if="isActiveNewsTextTranslated" />
          <IconTranslate v-else />
        </template>
      </div>
      <div
        v-if="!isCardOpened"
        class="news-card-header__button news-card-header__button-open"
        @click="isCardOpened = true"
      >
        <ArrowBottomIcon />
      </div>
      <div
        v-else
        class="news-card-header__button news-card-header__button-close"
        @click="isCardOpened = false"
      >
        <ArrowTopIcon />
      </div>
    </div>

    <div
      v-if="isCardOpened"
      class="news-card-body scroll-list-card"
    >
      <div class="full-card-header">
        <div class="full-card-header__date">
          {{ convertTimestampToData(props.item.date) }}
        </div>
        <div class="full-card-header-items">
          <div class="full-card-header-items__location">
            <LocationIcon />
            {{ props.item.cities[0]?.city }}
          </div>
          <div class="full-card-header-items__sentiment">
            <Component
              :is="props.item.sentiment > 0 ? GoodIcon : props.item.sentiment < 0 ? SadIcon : NeutralIcon"
              v-if="props.item.sentiment !== null"
            />
          </div>
        </div>
      </div>
      <div
        class="full-card-body"
        v-html="translatedNewsText"
      />
    </div>

    <div class="news-card-footer">
      <div class="news-card-footer__description">
        <div class="description__item">
          Feed url:
          <a
            :href="props.item.source"
            target="_blank"
          >{{ props.item.domain }}</a>
        </div>

        <div
          class="description__item"
          @click="saveIdToClipboard(props.item.id)"
        >
          id:
          <a>{{ props.item.id }}</a>
        </div>
      </div>
      <Chip
        class="news-card-footer__importance"
        :model-value="true"
        :use-cross-icon="false"
        :color="importanceList[props.item.importance].colorName"
      >
        {{ t('map_single_news_view.importance.' + importanceList[props.item.importance].name) }}
      </Chip>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use "@/assets/style/variables.scss";

.scroll-list-card {
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 27px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: variables.$blue-another;
    border: 10px solid transparent;
    background-clip: padding-box;
    border-radius: 30px;
  }
}

.news-card {
  display: flex;
  flex-direction: column;
  padding: 20px;
  border-radius: 8px;
  background: #FFFFFF;
  box-shadow: 0 4px 8px 0 #00000024;
  gap: 10px;

  &-body {
    display: flex;
    flex-direction: column;
    max-height: 589px;
    padding: 20px;
    border-radius: 8px;
    background: #F7F8FD;
    gap: 10px;

    .full-card-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 6px;

      &__date {
        font-size: 14px;
        font-weight: 400;
        color: #808080;
      }

      &-items {
        display: flex;
        gap: 5px;

        &__location {
          text-align: center;
          display: inline;
          color: #7B87A5;
          background: #FFF;
          padding: 4px 6px;
          border-radius: 20px;
          height: 24px;
          font-size: 12px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }

    .full-card-body {
      line-height: 19.6px;
    }

  }

  &-header {
    display: flex;
    align-items: center;
    gap: 6px;

    &__title {
      font-size: 16px;
      line-height: 22px;
      font-weight: 500;
      flex: 1;
    }

    &__button {
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 36px;
      height: 36px;
      background: var(--button-secondary-neutral-default);
      padding: 10px;
      cursor: pointer;
      border-radius: 50%;
      color: var(--icon-icon-primary);

      &:hover {
        color: #1F9AFF;
      }

      &--active {
        background: var(--icon-icon-primary);

        &:hover {
          background: var(--purple-purple80);
        }
      }

      &-open {
        background: #F6F7FB;
      }

      &-close {
        color: #1F9AFF;
      }
    }
  }

  &-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;

    &__description {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      grid-gap: 10px;


      .description__item {
        display: inline;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        font-size: 12px;
        line-height: 14px;
        font-weight: 400;
        color: #7B87A5;

        a {
          font-size: 12px;
          font-weight: 400;
          cursor: pointer;
          text-decoration: none;
          color: #1F9AFF;
        }
      }
    }

    &__importance {
      white-space: nowrap;
      padding: 5px 12px 5px 12px;
      border-radius: 22px;
    }
  }
}

</style>